import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async (options = {}) => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token');

    if (!token) {
      return [];
    }

    const response = await api.get('/notifications', {
      params: {
        platform: options.platform || 'website',
        locale: options.locale || localStorage.getItem('mojo_lang') || 'en',
      },
    });
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    setNotifications(rows);
    setUnreadCount(rows.filter((row) => !row.read_at).length);
    return rows;
  }, []);

  const loadUnreadCount = useCallback(async () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('access_token');

    if (!token) {
      setUnreadCount(0);
      return 0;
    }

    const response = await api.get('/notifications/unread-count');
    const count = Number(response.data?.unread || 0);
    setUnreadCount(count);
    return count;
  }, []);

  useEffect(() => {
    loadNotifications().catch(() => {});
  }, [loadNotifications]);

  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      ...notification,
      timestamp: new Date()
    };
    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show toast notification
    switch (notification.type) {
      case 'success':
        toast.success(notification.message);
        break;
      case 'error':
        toast.error(notification.message);
        break;
      case 'warning':
        toast.warning(notification.message);
        break;
      case 'info':
        toast.info(notification.message);
        break;
      default:
        toast(notification.message);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === id ? { ...n, read: true, read_at: n.read_at || new Date().toISOString() } : n
      )
    );
    setUnreadCount(prev => Math.max(prev - 1, 0));
    api.post(`/notifications/${id}/read`).catch(() => {});
  };

  const markAllRead = useCallback(async () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true, read_at: n.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      await api.post('/notifications/read-all');
    } catch (error) {
      loadUnreadCount().catch(() => {});
    }
  }, [loadUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    removeNotification,
    clearNotifications,
    markAsRead,
    markAllRead,
    loadNotifications,
    loadUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
